import React, { useState } from 'react';
import { CloseButton, Group, Input, Stack, Text, Tooltip, Divider } from '@mantine/core';
import Pagination from 'ps-components/Pagination/Pagination';
import { IOrgJob, ISourceMatch, ISubmitMatchesResponse } from '../PropagateMatchesTypes';

type Props = {
  activeTab: string;
  closeLoader: () => void;
  fetchSurveyJobMatchData: (params: {
    resetSelectedMatches?: boolean;
    surveyJobMatch?: ISourceMatch;
    matchesMade?: ISubmitMatchesResponse;
  }) => void;
  index: number;
  openLoader: () => void;
  orgJobs: IOrgJob[];
  setSurveyJobMatches: React.Dispatch<React.SetStateAction<ISourceMatch[]>>;
  surveyJobMatch: ISourceMatch;
  surveyJobMatches: ISourceMatch[];
  updateSurveyJobMatch: (surveyJobMatch: ISourceMatch, updates: Partial<ISourceMatch>) => ISourceMatch[];
};

const MatchDescriptor: React.FC<Props> = ({
  closeLoader,
  fetchSurveyJobMatchData,
  index,
  openLoader,
  setSurveyJobMatches,
  surveyJobMatch,
  updateSurveyJobMatch,
}) => {
  const {
    currency,
    filter = '',
    jobTitle,
    limit,
    offset,
    surveyCode,
    surveyJobCode,
    surveyPublisher,
    totalCount,
    jobScope
  } = surveyJobMatch;

  const [input, setInput] = useState('');

  const clearInput = () => {
    updateMatchAndFetchData({ filter: '' });
    setInput('');
  };

  const handleKeyUpOnMatch = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const target = event.target as HTMLInputElement;
      updateMatchAndFetchData({ filter: target.value });
    }
  };

  const pageSurveys = (limit: number, offset: number) => {
    updateMatchAndFetchData({ limit, offset });
  };

  const updateMatchAndFetchData = async (value: Partial<ISourceMatch>) => {
    const updatedMatches = updateSurveyJobMatch(surveyJobMatch, value);
    setSurveyJobMatches(updatedMatches);

    const match = updatedMatches.find(
      (match) => match.sourceMatchId === surveyJobMatch.sourceMatchId
    );
    if (match) {
      openLoader();
      await fetchSurveyJobMatchData({surveyJobMatch: match});
      closeLoader();
    }
  };

  return (
    <Stack>
      <Group
        align="center"
        bg="ghost.5"
        className="job-descriptor__bar"
        component="div"
        justify="space-between"
        p="15 20"
      >
        <Stack>
          <Text>{`Match ${index + 1}`}</Text>
          <Group>
            <Tooltip label={jobTitle}>
              <Text truncate='end' fz='h6' lh='1.2' fw='700' maw={'8vw'}>{jobTitle}</Text>
            </Tooltip>
            <Divider orientation='vertical' color='black' size='sm' />
            <Tooltip label={surveyJobCode}>
              <Text truncate='end' fz='h6' lh='1.2' fw='700' maw={'8vw'}>{surveyJobCode}</Text>
            </Tooltip>
            <Divider orientation='vertical' color='black' size='sm' />
            <Tooltip label={jobScope}>
              <Text truncate='end' fz='h6' lh='1.2' fw='700' maw={'8vw'}>{jobScope}</Text>
            </Tooltip>
            <Divider orientation='vertical' color='black' size='sm' />
            <Tooltip label={surveyCode}>
              <Text truncate='end' fz='h6' lh='1.2' fw='700' maw={'8vw'}>{surveyCode}</Text>
            </Tooltip>
            <Divider orientation='vertical' color='black' size='sm' />
            <Tooltip label={surveyPublisher}>
              <Text truncate='end' fz='h6' lh='1.2' fw='700' maw={'8vw'}>{surveyPublisher}</Text>
            </Tooltip>
            <Divider orientation='vertical' color='black' size='sm' />
            <Tooltip label={currency}>
              <Text truncate='end' fz='h6' lh='1.2' fw='700' maw={'4vw'}>{currency}</Text>
            </Tooltip>
          </Group>
        </Stack>
        <Stack align="flex-end">
          <Input
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyUp={handleKeyUpOnMatch}
            placeholder="Search survey code or survey title"
            rightSectionPointerEvents="all"
            rightSection={filter && <CloseButton aria-label="Clear input" onClick={clearInput} />}
            size="md"
            value={input}
            w={450}
          />
          <Pagination
            offset={offset}
            pageAction={pageSurveys}
            step={limit}
            totalCount={totalCount}
          />
        </Stack>
      </Group>
    </Stack>
  );
};

export default MatchDescriptor;
